import { useCallback, useEffect, useState } from "react"

import {
  MetricResultCard,
  yesNoBadgeEmptyClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import {
  Button,
  Card,
  CopyableId,
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTablePrimaryCell,
  ListTableRow,
} from "@coval/ui"
import { AlertTriangle, Info, RefreshCw } from "lucide-react"

import type {
  TestRecentSqlMetricResponse,
  TestSqlMetricSimulationResult,
} from "@/types_api/backend/transport/test_recent_sql_metric_response"
import { formatNumber } from "@/utils/formatting/format-number"

import { useTestSqlMetricTransport } from "../routes/useTestSqlMetricTransport"

interface SqlTestAreaProps {
  sqlQuery: string
  metricDisplayName: string
  aggregationMethod?: string
  unit?: string
}

export function SqlTestArea({
  sqlQuery,
  metricDisplayName,
  aggregationMethod,
  unit,
}: SqlTestAreaProps) {
  const { mutate, reset, data, error, isPending } = useTestSqlMetricTransport()

  const runTest = useCallback(() => {
    mutate({
      sql_query: sqlQuery,
      aggregation_method: aggregationMethod,
      unit,
    })
  }, [mutate, sqlQuery, aggregationMethod, unit])

  useEffect(() => {
    runTest()
    return () => {
      reset()
    }
  }, [runTest, reset])

  return (
    <section
      aria-label="SQL metric test area"
      className="space-y-3 rounded-md border-2 border-dashed border-coval-border p-3"
    >
      {isPending ? (
        <div className="flex items-center gap-2 text-sm text-coval-text-muted">
          <RefreshCw className="size-4 animate-spin" />
          Testing metric…
        </div>
      ) : error ? (
        <SqlTestTransportError onRetry={runTest} />
      ) : data ? (
        <SqlTestResult result={data} metricDisplayName={metricDisplayName} />
      ) : null}
    </section>
  )
}

function SqlTestTransportError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-md border border-coval-danger-border bg-coval-danger-surface px-3 py-2 text-sm text-coval-danger-on-surface"
    >
      <span>Couldn&apos;t reach the test endpoint.</span>
      <Button type="button" variant="secondary" onClick={onRetry}>
        <RefreshCw />
        Retry
      </Button>
    </div>
  )
}

function SqlTestResult({
  result,
  metricDisplayName,
}: {
  result: TestRecentSqlMetricResponse
  metricDisplayName: string
}) {
  const simulationResults = result.simulation_results ?? []

  // The top-level compatibility fields mirror the newest result, so prefer the array to avoid hiding other successful simulations.
  if (simulationResults.length > 0) {
    return (
      <RecentSimulationResults
        results={simulationResults}
        metricDisplayName={metricDisplayName}
      />
    )
  }

  if (result.requires_simulations) {
    return <SqlTestEmptyState message={result.error} />
  }

  return (
    <Card
      aria-label={`Simulation metric preview for ${metricDisplayName}`}
      className="overflow-hidden"
    >
      <SimulationMetricPreview
        result={result}
        metricDisplayName={metricDisplayName}
      />
    </Card>
  )
}

function RecentSimulationResults({
  results,
  metricDisplayName,
}: {
  results: TestSqlMetricSimulationResult[]
  metricDisplayName: string
}) {
  const defaultSimulation = results[0]
  const [requestedSimulationId, setRequestedSimulationId] = useState(
    defaultSimulation?.simulation_output_id ?? "",
  )
  const selectedSimulation =
    results.find(
      (result) => result.simulation_output_id === requestedSimulationId,
    ) ?? defaultSimulation

  if (!selectedSimulation) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="max-h-80 overflow-y-auto rounded-md">
        <ListTable aria-label="Recent simulations tested">
          <ListTableHeader>
            <ListTableRow>
              <ListTableHead>Run</ListTableHead>
              <ListTableHead className="w-36">Simulation</ListTableHead>
              <ListTableHead className="w-40">Date</ListTableHead>
            </ListTableRow>
          </ListTableHeader>
          <ListTableBody>
            {results.map((simulation) => {
              const isSelected =
                simulation.simulation_output_id ===
                selectedSimulation.simulation_output_id
              const selectSimulation = () => {
                setRequestedSimulationId(simulation.simulation_output_id)
              }

              return (
                <ListTableRow
                  key={simulation.simulation_output_id}
                  tabIndex={0}
                  aria-selected={isSelected}
                  className={`cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-coval-border-active ${isSelected ? "bg-surface-selected" : ""}`}
                  onClick={selectSimulation}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      selectSimulation()
                    }
                  }}
                >
                  <ListTablePrimaryCell
                    label={simulation.run_display_name || "Untitled run"}
                  />
                  <ListTableCell>
                    <CopyableId
                      id={simulation.simulation_output_id}
                      label="Copy Simulation ID"
                    />
                  </ListTableCell>
                  <ListTableCell className="whitespace-nowrap text-coval-text-muted">
                    <time dateTime={simulation.simulation_created_at}>
                      {formatSimulationDate(simulation.simulation_created_at)}
                    </time>
                  </ListTableCell>
                </ListTableRow>
              )
            })}
          </ListTableBody>
        </ListTable>
      </div>
      <Card
        aria-label={`Simulation metric preview for ${metricDisplayName}`}
        className="overflow-hidden"
      >
        <SimulationMetricPreview
          result={selectedSimulation}
          metricDisplayName={metricDisplayName}
        />
      </Card>
    </div>
  )
}

function SqlTestEmptyState({ message }: { message?: string | null }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-coval-border bg-surface-1 px-3 py-3 text-sm text-coval-text-muted"
    >
      <Info className="mt-0.5 size-4 shrink-0" />
      <span>
        {message ||
          "Run a simulation in this workspace, then test the query again."}
      </span>
    </div>
  )
}

type SqlMetricEvaluationResult = Pick<
  TestRecentSqlMetricResponse,
  "value" | "unit" | "error"
>

function SimulationMetricPreview({
  result,
  metricDisplayName,
}: {
  result: SqlMetricEvaluationResult
  metricDisplayName: string
}) {
  if (result.error) {
    return (
      <div
        role="alert"
        className="m-4 flex items-start gap-2 rounded-md border border-coval-danger-border bg-coval-danger-surface px-3 py-2 text-sm text-coval-danger-on-surface"
      >
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span className="whitespace-pre-wrap">{result.error}</span>
      </div>
    )
  }

  return (
    <MetricResultCard
      title={metricDisplayName}
      description="Preview of how this metric result will appear on a simulation."
      valueNode={
        <span className={`${yesNoBadgeShapeClasses} ${yesNoBadgeEmptyClasses}`}>
          {formatNullableMetricValue(result.value, result.unit)}
        </span>
      }
      borderless
    />
  )
}

function formatNullableMetricValue(
  value: number | null | undefined,
  unit?: string | null,
): string {
  return typeof value === "number" && Number.isFinite(value)
    ? formatMetricValue(value, unit)
    : "—"
}

function formatMetricValue(value: number, unit?: string | null): string {
  const unitSuffix = unit ? (unit === "%" ? unit : ` ${unit}`) : ""
  return `${formatMetricNumber(value, unit)}${unitSuffix}`
}

function formatMetricNumber(value: number, unit?: string | null): string {
  return unit === "s" ? value.toFixed(2) : formatNumber(value)
}

function formatSimulationDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date)
}
